import { RUSH_ROUND_MS, TURN_TIMEOUT_INIT_MS, TYPE } from "@gaoya/shared";
import { serverNow, type UiState } from "../../game/state.js";
import {
  C,
  capsuleBox,
  clipName,
  drawArt,
  drawCircleAvatar,
  drawPanel,
  drawTag,
  fillRound,
  pageHeader,
  roundPath,
  sub,
} from "../theme.js";
import { layoutPrivate, privateWaitLayout } from "../../ui/hall.js";

export function drawPrivate(ctx: CanvasRenderingContext2D, ui: UiState, w: number, h: number, now = Date.now()): void {
  const cap = capsuleBox(ui, w);
  if (ui.privateMode === "wait") {
    const modeLabel =
      ui.mode === "RUSH"
        ? `${ui.lobbyMaxSeats} 人抢答 · 每轮 ${Math.round(RUSH_ROUND_MS / 1000)} 秒`
        : `1 对 1 · 每手 ${Math.round(TURN_TIMEOUT_INIT_MS / 1000)} 秒`;
    pageHeader(ctx, ui, w, "好友雅间", modeLabel);
    const g = privateWaitLayout(w, h, ui);
    const seated = ui.mode === "RUSH" ? ui.lobbySeats.length : 1;
    drawRoomCode(ctx, ui, g.copy);

    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = C.ink;
    ctx.font = `700 15px ${TYPE.serif}`;
    ctx.fillText("雅间席位", 16, g.gridY - 17);
    ctx.fillStyle = C.ink2;
    ctx.font = `12px ${TYPE.sans}`;
    ctx.textAlign = "right";
    ctx.fillText(`${seated}/${g.seats} 已入席`, w - 16, g.gridY - 17);

    for (let i = 0; i < g.cells.length; i++) {
      const cell = g.cells[i];
      if (!cell) continue;
      const seat =
        ui.mode === "RUSH"
          ? (ui.lobbySeats[i] ?? null)
          : i === 0
            ? { id: ui.userId, nickname: ui.nickname, ready: true }
            : null;
      const host = ui.mode !== "RUSH" ? i === 0 : !!seat && (seat.id === ui.lobbyHostId || (!ui.lobbyHostId && i === 0));
      drawSeat(ctx, ui, cell, seat, host, now);
    }

    const hint = waitHint(ui, now, g.seats);
    const urgent = ui.lobbyReadyDeadline > 0 || ui.lobbyCountdownDeadline > 0;
    sub(ctx, hint, w / 2, (g.ready?.y ?? g.back.y) - 18, 13, urgent ? C.seal : C.ink2);
    return;
  }

  if (ui.privateMode === "join") {
    pageHeader(ctx, ui, w, "持号入座", "输入好友提供的 6 位房号");
    drawRoomCodeSlots(ctx, ui, w, cap.bottom + 24);
    return;
  }

  pageHeader(ctx, ui, w, "好友雅间", "自建雅间或持号入座");
  const btns = layoutPrivate(w, h, ui);
  const first = btns.find((b) => b.id === "create-1v1");
  const top = cap.bottom + 14;
  const bottom = (first?.y ?? h * 0.3) - 18;
  const ch = bottom - top;
  if (ch >= 80) {
    const r = { x: 16, y: top, w: w - 32, h: ch };
    drawPanel(ctx, r.x, r.y, r.w, r.h, 16, C.card);
    ctx.save();
    roundPath(ctx, r.x, r.y, r.w, r.h, 16);
    ctx.clip();
    ctx.fillStyle = C.goldSoft;
    ctx.beginPath();
    ctx.arc(r.x + r.w - 64, r.y + r.h * 0.55, r.h * 0.55, 0, Math.PI * 2);
    ctx.fill();
    const ah = Math.min(r.h + 6, 150);
    drawArt(ctx, "char-scholar-cut", r.x + r.w - ah - 2, r.y + r.h - ah + 6, ah, ah);
    ctx.restore();
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const ty = r.y + r.h / 2;
    ctx.fillStyle = C.ink;
    ctx.font = `700 20px ${TYPE.serif}`;
    ctx.fillText("以文会友", r.x + 20, ty - 18);
    ctx.fillStyle = C.ink2;
    ctx.font = `13px ${TYPE.sans}`;
    ctx.fillText("开一间雅座，分享给微信好友", r.x + 20, ty + 8);
    ctx.fillText("凭 6 位房号即可入席", r.x + 20, ty + 28);
  }
  const join = btns.find((b) => b.id === "join");
  if (first) {
    ctx.fillStyle = C.ink3;
    ctx.font = `12px ${TYPE.sans}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (join) ctx.fillText("已有房号？点「持号入座」", w / 2, join.y + join.h + 20);
  }
}

function drawRoomCode(ctx: CanvasRenderingContext2D, ui: UiState, r: { x: number; y: number; w: number; h: number }): void {
  const pressed = ui.pointerDown && ui.pointerX >= r.x && ui.pointerX <= r.x + r.w && ui.pointerY >= r.y && ui.pointerY <= r.y + r.h;
  drawPanel(ctx, r.x, r.y + (pressed ? 1 : 0), r.w, r.h, 16, C.card);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.ink2;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText("房号", r.x + 18, r.y + 22);
  const code = ui.roomCode || "------";
  ctx.fillStyle = C.seal;
  ctx.font = `700 28px ${TYPE.serif}`;
  let x = r.x + 18;
  for (const ch of code) {
    ctx.fillText(ch, x, r.y + 48);
    x += ctx.measureText(ch).width + 5;
  }
  drawTag(ctx, "复制房号", r.x + r.w - 16, r.y + (r.h - 30) / 2, 30, C.sealSoft, C.seal, "right", 13);
}

function waitHint(ui: UiState, now: number, seats: number): string {
  if (ui.mode !== "RUSH") return "点虚席邀请好友 · 友人入座后自动开局";
  const n = ui.lobbySeats.length;
  const readyN = ui.lobbySeats.filter((s) => s.ready).length;
  if (ui.lobbyReadyDeadline > 0) {
    const left = Math.max(0, Math.ceil((ui.lobbyReadyDeadline - serverNow(ui, now)) / 1000));
    return `人满请准备 · ${left} 秒未备将请出`;
  }
  if (ui.lobbyCountdownDeadline > 0) {
    const left = Math.max(0, Math.ceil((ui.lobbyCountdownDeadline - serverNow(ui, now)) / 1000));
    return `全员已备 · ${left} 秒后开抢`;
  }
  if (n < seats) return `候人 ${n}/${seats} · 点虚席邀请好友`;
  return `${readyN}/${seats} 已准备`;
}

function drawSeat(
  ctx: CanvasRenderingContext2D,
  ui: UiState,
  r: { x: number; y: number; w: number; h: number },
  seat: { id: string; nickname: string; ready?: boolean } | null,
  host: boolean,
  now: number,
): void {
  const cx = r.x + r.w / 2;
  if (!seat) {
    fillRound(ctx, r.x, r.y, r.w, r.h, 16, "rgba(255,252,245,0.55)");
    ctx.save();
    ctx.strokeStyle = "rgba(184,58,36,0.35)";
    ctx.lineWidth = 1.2;
    ctx.setLineDash([5, 4]);
    roundPath(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 16);
    ctx.stroke();
    ctx.restore();
    const pulse = 0.5 + 0.5 * Math.sin(now / 400);
    const cy = r.y + r.h / 2 - 14;
    fillRound(ctx, cx - 20, cy - 20, 40, 40, 20, `rgba(246,226,218,${0.7 + pulse * 0.3})`);
    ctx.strokeStyle = C.seal;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(cx - 7, cy);
    ctx.lineTo(cx + 7, cy);
    ctx.moveTo(cx, cy - 7);
    ctx.lineTo(cx, cy + 7);
    ctx.stroke();
    ctx.fillStyle = C.ink;
    ctx.font = `700 14px ${TYPE.serif}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("虚席以待", cx, cy + 36);
    ctx.fillStyle = C.seal;
    ctx.font = `12px ${TYPE.sans}`;
    ctx.fillText("邀请微信好友", cx, cy + 55);
    return;
  }
  drawPanel(ctx, r.x, r.y, r.w, r.h, 16, C.card);
  if (host) drawTag(ctx, "房主", r.x + 10, r.y + 10, 20, C.seal, "#FFFFFF", "left", 11);
  if (ui.mode === "RUSH") {
    const ready = !!seat.ready;
    drawTag(ctx, ready ? "已准备" : "未准备", r.x + r.w - 10, r.y + 10, 20, ready ? C.jadeSoft : C.well, ready ? C.jade : C.ink2, "right", 11);
  }
  const img = seat.id === ui.userId ? ui.avatarImg : ui.peerImgs[seat.id] ?? null;
  drawCircleAvatar(ctx, img, cx, r.y + r.h / 2 - 6, 24, seat.nickname);
  ctx.fillStyle = C.ink;
  ctx.font = `700 14px ${TYPE.serif}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(clipName(ctx, seat.nickname + (seat.id === ui.userId ? "（我）" : ""), r.w - 16), cx, r.y + r.h - 20);
}

function drawRoomCodeSlots(ctx: CanvasRenderingContext2D, ui: UiState, w: number, y: number): void {
  const slots = 6;
  const gap = 8;
  const slotW = Math.min(48, (w - 32 - gap * (slots - 1)) / slots);
  const slotH = 60;
  const total = slots * slotW + gap * (slots - 1);
  const x0 = (w - total) / 2;
  const digits = ui.joinInput;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let i = 0; i < slots; i++) {
    const sx = x0 + i * (slotW + gap);
    const ch = digits[i] ?? "";
    const active = i === digits.length;
    drawPanel(ctx, sx, y, slotW, slotH, 12, ch ? C.card : "rgba(255,252,245,0.7)", active ? C.seal : C.line);
    if (ch) {
      ctx.fillStyle = C.ink;
      ctx.font = `700 28px ${TYPE.serif}`;
      ctx.fillText(ch, sx + slotW / 2, y + slotH / 2 + 1);
    } else if (active) {
      fillRound(ctx, sx + slotW / 2 - 8, y + slotH - 14, 16, 3, 1.5, C.seal);
    }
  }
  ctx.fillStyle = C.ink3;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(`${digits.length}/6 位`, w / 2, y + slotH + 22);
}
